/**
 * PowerSync Facade — Electron Renderer
 *
 * Proxies every PowerSync operation to the main process (where the real
 * @powersync/node database lives) via `window.powersync`, exposed by
 * preload.ts. Implements `PowerSyncFacade` — the same interface the web
 * build's wrapper around the real @powersync/web instance implements — so
 * callers that already import `db` from `./database` (ipcBridge.ts,
 * authService.ts, useSyncStatus.ts, usePendingCrudCount.ts,
 * SyncStatusChip.tsx, localOrderOps.ts) need no changes.
 */

import type { RawPowerSyncStatus } from '@/renderer/types/powersync';

import { ElectronTransactionFacade } from './electronTransaction';
import {
  PowerSyncFacade,
  PowerSyncQueryResultLike,
  PowerSyncTransactionFacade,
  toQueryResultRows,
} from './facadeTypes';
import { syncEvents } from './supabaseConnector';

const DEFAULT_STATUS: RawPowerSyncStatus = {
  connected: false,
  connecting: false,
  hasSynced: false,
  lastSyncedAt: null,
  dataFlowStatus: {
    uploading: false,
    downloading: false,
    downloadError: null,
    uploadError: null,
  },
};

export class ElectronPowerSyncFacade implements PowerSyncFacade {
  private status: RawPowerSyncStatus = DEFAULT_STATUS;

  private statusListeners = new Set<(s: RawPowerSyncStatus) => void>();

  private changeListeners = new Set<(tables: string[]) => void>();

  constructor() {
    const bridge = window.powersync!;

    (async () => {
      try {
        this.status = await bridge.getStatus();
        this.statusListeners.forEach((cb) => cb(this.status));
      } catch (err) {
        console.error('[PowerSync] getStatus failed:', err);
      }
    })();

    bridge.onStatusChange((s: RawPowerSyncStatus) => {
      this.status = s;
      this.statusListeners.forEach((cb) => cb(this.status));
    });

    bridge.onChange((tables: string[]) => {
      this.changeListeners.forEach((cb) => cb(tables));
    });

    // Relay main's ghost-order bill-conflict notification onto the shared
    // renderer event bus (RunningOrderLiveSync.tsx listens on this).
    bridge.onBillConflict((detail: { message: string }) => {
      syncEvents.dispatchEvent(new CustomEvent('bill-conflict', { detail }));
    });

    // Relay main's generic "a change couldn't be saved and was discarded"
    // notification (fatal Postgres error, or a transaction that exhausted
    // its retry ceiling) — see supabaseConnector.ts's discardAndCleanup().
    bridge.onSyncDiscarded((detail: { message: string }) => {
      syncEvents.dispatchEvent(new CustomEvent('sync-discarded', { detail }));
    });

    // Relay main's "uploads are held because the company is disabled /
    // subscription paused" notification — nothing is discarded, the queue
    // resumes on its own once the company is re-enabled.
    bridge.onSyncPaused((detail: { message: string }) => {
      syncEvents.dispatchEvent(new CustomEvent('sync-paused', { detail }));
    });

    // Relay main's terminal "the native sync engine cannot run" signal so the
    // app can show a persistent banner and switch to the @powersync/web
    // fallback — see database.ts (main) emitSyncEngineFailed().
    bridge.onSyncEngineFailed?.(
      (detail: { message: string; stage: string }) => {
        syncEvents.dispatchEvent(
          new CustomEvent('sync-engine-failed', { detail }),
        );
      },
    );

    // Relay main's "a sync stream repeated the same error too many times"
    // signal. Distinct from 'sync-engine-failed' above — deliberately NOT
    // wired to the web-fallback switch, since this means a recoverable
    // server-side sync-rule error, not a broken native engine (see
    // powersyncHandlers.ts's trackSyncError). Dispatched for any future
    // lightweight UI/diagnostics use; SyncEngineFallbackBanner intentionally
    // does not listen for it.
    bridge.onSyncStreamErrorRepeated?.(
      (detail: { message: string; stage: string }) => {
        syncEvents.dispatchEvent(
          new CustomEvent('sync-stream-error-repeated', { detail }),
        );
      },
    );
  }

  get currentStatus(): RawPowerSyncStatus {
    return this.status;
  }

  // eslint-disable-next-line class-methods-use-this
  async execute(
    sql: string,
    params?: unknown[],
  ): Promise<PowerSyncQueryResultLike> {
    const result = await window.powersync!.execute(sql, params);
    return {
      rowsAffected: result.rowsAffected,
      insertId: result.insertId,
      rows: toQueryResultRows(result.rows),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async get<T = Record<string, unknown>>(
    sql: string,
    params?: unknown[],
  ): Promise<T> {
    return window.powersync!.get(sql, params) as Promise<T>;
  }

  // eslint-disable-next-line class-methods-use-this
  async getAll<T = Record<string, unknown>>(
    sql: string,
    params?: unknown[],
  ): Promise<T[]> {
    return window.powersync!.getAll(sql, params) as Promise<T[]>;
  }

  // eslint-disable-next-line class-methods-use-this
  async getUploadQueueStats(): Promise<{ count: number }> {
    return window.powersync!.getUploadQueueStats();
  }

  // Keeps a real main-process writeTransaction open for the lifetime of the
  // begin/…/commit sequence, so every tx.execute/tx.get call from `callback`
  // happens inside the same SQLite transaction — see powersyncHandlers.ts.
  // eslint-disable-next-line class-methods-use-this
  async writeTransaction<T>(
    callback: (tx: PowerSyncTransactionFacade) => Promise<T>,
  ): Promise<T> {
    const { txId } = await window.powersync!.txBegin();
    const tx = new ElectronTransactionFacade(txId);
    try {
      const result = await callback(tx);
      await window.powersync!.txCommit(txId);
      return result;
    } catch (err) {
      await window.powersync!.txRollback(
        txId,
        err instanceof Error ? err.message : String(err),
      );
      throw err;
    }
  }

  registerListener(listener: {
    statusChanged?: (s: RawPowerSyncStatus) => void;
  }): () => void {
    if (!listener.statusChanged) return () => {};
    const cb = listener.statusChanged;
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  }

  // `_options` (e.g. `{ tables }`) mirrors the web SDK's onChange signature
  // for call-site compatibility, but is unused here — main pushes every
  // changed table and ipcBridge.ts's usePowerSyncChange already filters by
  // table name on the receiving end.
  // eslint-disable-next-line class-methods-use-this
  onChange(
    listener: { onChange: (event: { changedTables: string[] }) => void },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _options?: unknown,
  ): () => void {
    const cb = (tables: string[]) =>
      listener.onChange({ changedTables: tables });
    this.changeListeners.add(cb);
    return () => this.changeListeners.delete(cb);
  }

  // Connection lifecycle is owned by main (driven by relayed session events);
  // this only triggers a manual reconnect attempt (e.g. the "Reconnect"
  // button in SyncStatusChip). The argument is accepted for API
  // compatibility with the web SDK's `db.connect(connector)` call sites but
  // is unused — main already holds its own connector instance.
  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
  async connect(_connector?: unknown): Promise<void> {
    await window.powersync!.reconnect();
  }
}
